/**
 * SearchSpring Collection Controller
 *
 * Orchestrator for SS-powered collection pages. Handles:
 * - Initial search request with collection handle + URL params
 * - Results rendering via shared card renderer (ss-client.js)
 * - Filter/sort/pagination refresh cycle
 * - Error and no-results states
 *
 * Depends on ss-client.js (defer IIFE) for SSClient + card renderer.
 * No native fallback: if SS fails, the page shows an error state.
 */

// =============================================================================
// Collection Controller
// =============================================================================

class SSCollectionController {
  #client;
  #config;
  #skeletonContainer;
  #ssGrid;
  #ssGridList;
  #seenGids = null;

  constructor() {
    const configEl = document.getElementById('ss-config');
    if (!configEl) return;

    try {
      this.#config = JSON.parse(configEl.textContent);
    } catch {
      console.error('[SS] Failed to parse config');
      return;
    }

    this.#skeletonContainer = document.getElementById('ss-skeleton-container');
    this.#ssGrid = document.getElementById('ss-results-grid');
    this.#ssGridList = this.#ssGrid?.querySelector('ul[ref="grid"]');

    if (!this.#config.enabled || !this.#config.siteId || !this.#config.collectionHandle) {
      return;
    }

    this.#client = window.createSSClient({ siteId: this.#config.siteId });

    if (this.#config.consolidateSiblings) {
      this.#seenGids = new Set();
    }
  }

  get seenGids() {
    return this.#seenGids;
  }

  get enabled() {
    return !!this.#client;
  }

  async init() {
    if (!this.enabled) {
      this.#showError('SearchSpring is not configured for this page.');
      return;
    }

    const params = this.#buildSearchParams();
    const response = await this.#client.search(params);

    if (!response) {
      this.#showError();
      return;
    }

    if (response.results && response.results.length > 0) {
      this.#renderResults(response);
    } else {
      this.#showNoResults(response);
    }
  }

  async refresh(newParams = {}) {
    if (!this.enabled) return;

    if (this.#ssGridList) {
      this.#ssGridList.style.opacity = '0.5';
      this.#ssGridList.style.pointerEvents = 'none';
    }

    const params = { ...this.#buildSearchParams(), ...newParams };
    const response = await this.#client.search(params);

    if (this.#ssGridList) {
      this.#ssGridList.style.opacity = '';
      this.#ssGridList.style.pointerEvents = '';
    }

    if (!response) {
      this.#showError();
      return;
    }

    if (response.results && response.results.length > 0) {
      this.#renderResults(response);
    } else {
      this.#showNoResults(response);
    }
  }

  lastResponse = null;

  /**
   * Fetch a specific page for infinite scroll. Returns raw API response.
   * @param {number} pageNum
   * @returns {Promise<object|null>}
   */
  async fetchPage(pageNum) {
    if (!this.enabled) return null;

    const params = this.#buildSearchParams();
    params.page = String(pageNum);
    return this.#client.search(params);
  }

  #renderResults(response) {
    this.lastResponse = response;

    // Remove any existing empty state
    this.#ssGrid?.querySelector('.main-collection-grid__empty')?.remove();

    const page = response.pagination?.page || response.pagination?.currentPage || 1;

    // Reset the dedup set on every fresh render (initial load, filter/sort change).
    // Infinite-scroll page appends will add to this same Set so duplicates get
    // suppressed across pages.
    if (this.#seenGids) this.#seenGids.clear();

    if (this.#ssGridList) {
      this.#ssGridList.style.display = '';
      this.#ssGridList.innerHTML = window.renderProductGrid(response.results, {
        ...this.#config,
        page,
        seenGids: this.#seenGids,
      });
    }

    if (this.#skeletonContainer) this.#skeletonContainer.hidden = true;
    if (this.#ssGrid) this.#ssGrid.removeAttribute('hidden');

    document.dispatchEvent(
      new CustomEvent('ss:results-rendered', { detail: { response } })
    );
  }

  #showError(message) {
    if (this.#skeletonContainer) this.#skeletonContainer.hidden = true;
    if (this.#ssGrid) {
      this.#ssGrid.removeAttribute('hidden');
      if (this.#ssGridList) {
        const container = this.#ssGridList.parentElement;
        if (container) {
          const heading = message || 'Unable to load products';
          container.innerHTML =
            '<div class="main-collection-grid__empty">' +
            '<h2 class="main-collection-grid__empty-title h2">' + heading + '</h2>' +
            '<p>Please try <a href="javascript:window.location.reload()" class="main-collection-grid__empty-link">reloading the page</a>.</p>' +
            '</div>';
        }
      }
    }
    console.error('[SS]', message || 'API request failed');
  }

  #showNoResults(response) {
    this.lastResponse = response;

    if (this.#skeletonContainer) this.#skeletonContainer.hidden = true;
    if (this.#ssGrid) {
      this.#ssGrid.removeAttribute('hidden');

      // Hide the grid list, show empty state as sibling (matches Horizon's product-grid.liquid pattern)
      if (this.#ssGridList) {
        this.#ssGridList.style.display = 'none';
      }

      // Remove any existing empty state before creating new one
      this.#ssGrid.querySelector('.main-collection-grid__empty')?.remove();

      const collectionUrl = `/collections/${this.#config.collectionHandle}`;
      const emptyEl = document.createElement('div');
      emptyEl.className = 'main-collection-grid__empty';
      emptyEl.innerHTML =
        '<h2 class="main-collection-grid__empty-title h2">No products found</h2>' +
        '<p>Try removing some filters or <a href="' + collectionUrl + '" class="main-collection-grid__empty-link ss-zero-clear-all">clear all filters</a>.</p>';

      // Insert as sibling of the grid list
      this.#ssGridList.parentElement.appendChild(emptyEl);

      // Bind clear-all click
      emptyEl.querySelector('.ss-zero-clear-all')?.addEventListener('click', (e) => {
        e.preventDefault();
        window.SSURLState.writeToURL({});
        this.refresh({});
      });
    }

    document.dispatchEvent(
      new CustomEvent('ss:results-rendered', { detail: { response } })
    );
  }

  #buildSearchParams() {
    const params = {
      'bgfilter.collection_handle': this.#config.collectionHandle,
      resultsPerPage: this.#config.resultsPerPage || 24,
    };

    const url = new URL(window.location.href);
    for (const [key, value] of url.searchParams) {
      // When infinite scroll is active, ignore URL page param — always start from page 1.
      // The infinite scroll module handles restoring position on back navigation.
      if (key === 'page' && this.#config.enableInfiniteScroll) continue;

      // Never forward excluded params (filter.collection_handle) from the URL.
      if (window.SSURLState?.isExcludedParam(key)) continue;

      if (key.startsWith('filter.') || key.startsWith('sort.') || key === 'page') {
        // Support multi-value filters (e.g., filter.size=M&filter.size=L)
        if (params[key] !== undefined) {
          if (Array.isArray(params[key])) {
            params[key].push(value);
          } else {
            params[key] = [params[key], value];
          }
        } else {
          params[key] = value;
        }
      }
    }

    return params;
  }
}

// =============================================================================
// Initialization
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {
  const config = document.getElementById('ss-config');
  if (!config) return;
  window.__ssCollection = new SSCollectionController();
  window.__ssCollection.init();

  // Initialize filter/sort/pagination modules (loaded via defer scripts)
  const filtersContainer = document.getElementById('ss-filters-container');
  const paginationContainer = document.getElementById('ss-pagination-container');

  let ssFilters = null;
  let ssSorting = null;
  let ssPagination = null;

  if (window.SSFilters && filtersContainer) {
    ssFilters = new window.SSFilters(filtersContainer);
  }
  if (window.SSSorting) {
    ssSorting = new window.SSSorting();
  }
  const ssConfig = JSON.parse(config.textContent);
  const useInfiniteScroll = ssConfig.enableInfiniteScroll && window.SSInfiniteScroll;

  if (!useInfiniteScroll && window.SSPagination) {
    ssPagination = new window.SSPagination();
  }

  let ssInfiniteScroll = null;
  if (useInfiniteScroll) {
    ssInfiniteScroll = new window.SSInfiniteScroll({
      controller: window.__ssCollection,
      config: ssConfig,
    });
  }

  let ssMobileDrawer = null;
  if (window.SSMobileDrawer) {
    ssMobileDrawer = new window.SSMobileDrawer();
  }

  // Initialize beacon tracking and GTM dataLayer
  if (window.__ssCollection?.enabled && ssConfig && window.SSBeacon) {
    const ssBeacon = new window.SSBeacon({
      siteId: ssConfig.siteId,
      pageLoadId: window.createSSClient({ siteId: ssConfig.siteId }).pageLoadId,
      module: 'category',
    });
    ssBeacon.init();
  }

  if (window.__ssCollection?.enabled && ssConfig && window.SSGtm) {
    const ssGtm = new window.SSGtm({
      collectionHandle: ssConfig.collectionHandle,
    });
    ssGtm.init();
  }

  // On every SS results render, update filters/sorting/pagination
  document.addEventListener('ss:results-rendered', (e) => {
    const response = e.detail?.response;
    if (!response) return;

    if (ssFilters) {
      ssFilters.render(response);

      // Render sorting inside the filter bar's sort container
      if (ssSorting && ssFilters.sortContainer) {
        ssSorting.render(response.sorting?.options || [], ssFilters.sortContainer);
      }
    }

    if (ssInfiniteScroll) {
      ssInfiniteScroll.onResultsRendered(response);
    } else if (ssPagination && paginationContainer) {
      ssPagination.render(response.pagination, paginationContainer);
    }

    if (ssMobileDrawer) {
      ssMobileDrawer.onResultsRendered(response);
    }
  });

  // Browser back/forward: re-fetch with URL params
  if (window.SSURLState) {
    window.SSURLState.onPopState((params) => {
      window.__ssCollection.refresh(params);
    });
  }
});
